import { useEffect, useState } from "react";
import { Box, Typography, Stack, IconButton, Tooltip, Divider } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CustomBox from "../../components/CustomBox/CustomBox";
import {
  getUserNotifications,
  setUserNotificationReadState,
  deleteUserNotification,
  UserNotification,
  userNotificationsEvents,
} from "../../utils/userNotifications";
import { useTranslation } from "react-i18next";

const Notifications = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<UserNotification[]>(() => getUserNotifications());

  useEffect(() => {
    const loadNotifications = () => {
      setNotifications(getUserNotifications());
    };

    loadNotifications();
    window.addEventListener(userNotificationsEvents.UPDATE_EVENT, loadNotifications);

    return () => {
      window.removeEventListener(userNotificationsEvents.UPDATE_EVENT, loadNotifications);
    };
  }, []);

  const formatNotificationTime = (isoDate: string) => {
    const createdAt = new Date(isoDate).getTime();
    const now = Date.now();
    const diffMs = Math.max(now - createdAt, 0);
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return t("notifications.justNow");
    if (diffMinutes < 60) return t("notifications.minutesAgo", { count: diffMinutes });
    if (diffHours < 24) return t("notifications.hoursAgo", { count: diffHours });
    return t("notifications.daysAgo", { count: diffDays });
  };

  const getNotificationText = (notification: UserNotification) => {
    if (notification.translationKey) {
      const documentTypeCode = notification.translationParams?.documentTypeCode;
      const documentType = documentTypeCode
        ? (t(`documents.types.${documentTypeCode}`) || documentTypeCode)
        : undefined;

      return t(notification.translationKey, {
        ...notification.translationParams,
        documentType,
        defaultValue: notification.text,
      });
    }

    return notification.text;
  };

  return (
    <Box>
      <CustomBox>
        <Typography variant="h5" sx={{ color: "text.primary", fontWeight: 600, mb: 2 }}>
          {t("notifications.title")}
        </Typography>
        <Divider sx={{ mb: 2, borderColor: "#474747ff" }} />

        {notifications.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("notifications.empty")}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {notifications.map((notification) => (
              <Box
                key={notification.id}
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  border: "1px solid #474747ff",
                  bgcolor: notification.read ? "transparent" : "action.hover",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" }}
                  >
                    {getNotificationText(notification)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatNotificationTime(notification.createdAt)}
                  </Typography>
                </Box>
                <Tooltip title={notification.read ? t("notifications.markAsUnread") : t("notifications.markAsRead")}>
                  <IconButton
                    size="small"
                    onClick={() => setUserNotificationReadState(notification.id, !notification.read)}
                    sx={{ color: "common.white" }}
                  >
                    {notification.read ? <MarkEmailUnreadIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("notifications.deleteNotification")}>
                  <IconButton
                    size="small"
                    onClick={() => deleteUserNotification(notification.id)}
                    sx={{ color: "common.white" }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        )}
      </CustomBox>
    </Box>
  );
};

export default Notifications;
